import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { useBylinePageAdf, type BylinePageAdf } from './useBylinePageAdf.ts';

import { collectSmartLinks } from '../utils/collectSmartLinks.ts';
import { getSmartLinkProductGroupLabel } from '../utils/groupSmartLinksByProduct.ts';
import { jsonLdToEnhancedSmartLink } from '../utils/jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

const ADF_WAIT_TIMEOUT_MS = 10000;

export type UseSmartLinksResult = {
	links: EnhancedSmartLink[];
	collectedForContentId: string | undefined;
	loading: boolean;
	refreshing: boolean;
	collectedAdf: BylinePageAdf['adf'];
	error: Error | null;
	refreshLinkUrl: (item: EnhancedSmartLink) => Promise<EnhancedSmartLink | undefined>;
};

const reportSmartLinkError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
	return errorObj;
};

type SmartLinkResponse = Parameters<typeof jsonLdToEnhancedSmartLink>[2];

const mergeRefreshedSmartLink = (
	link: EnhancedSmartLink,
	response: SmartLinkResponse,
): EnhancedSmartLink => {
	const refreshedLink = jsonLdToEnhancedSmartLink(link.url, link.occurrenceIndex, response);
	return {
		...refreshedLink,
		iconUrl: refreshedLink.iconUrl ?? link.iconUrl,
		product: link.product ?? refreshedLink.product,
		subproduct: link.subproduct ?? refreshedLink.subproduct,
	};
};

export function useSmartLinks(contentId?: string): UseSmartLinksResult {
	const document = useBylinePageAdf(contentId);
	const adf = document.contentId === contentId ? document.adf : null;
	const { connections, store } = useSmartLinkContext();
	const [result, setResult] = useState<{
		contentId: string | undefined;
		links: EnhancedSmartLink[];
		adf: BylinePageAdf['adf'];
		loading: boolean;
		refreshing: boolean;
		hasResolved: boolean;
		error: Error | null;
	}>({
		contentId,
		links: [],
		adf: null,
		loading: true,
		refreshing: false,
		hasResolved: false,
		error: null,
	});
	const linksRef = useRef<EnhancedSmartLink[]>([]);
	const currentContentId = useRef(contentId);
	currentContentId.current = contentId;
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	useEffect(() => {
		linksRef.current = [];
	}, [contentId]);

	useEffect(() => {
		let cancelled = false;
		setResult((previous) => {
			const samePage = previous.contentId === contentId;
			return {
				contentId,
				links: samePage ? previous.links : [],
				adf: samePage ? previous.adf : null,
				hasResolved: samePage && previous.hasResolved,
				loading: !samePage || !previous.hasResolved,
				refreshing: true,
				error: null,
			};
		});
		if (!adf) {
			const timeout = setTimeout(() => {
				setResult((previous) => ({ ...previous, loading: false, refreshing: false }));
			}, ADF_WAIT_TIMEOUT_MS);
			return () => clearTimeout(timeout);
		}

		collectSmartLinks(adf, storeRef.current, connections.client)
			.then((links) => {
				if (cancelled) return;
				linksRef.current = links;
				setResult({
					contentId,
					links,
					adf,
					loading: false,
					refreshing: false,
					hasResolved: true,
					error: null,
				});
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				const error = reportSmartLinkError(err);
				setResult((previous) => ({ ...previous, loading: false, refreshing: false, error }));
			});
		return () => {
			cancelled = true;
		};
	}, [adf, contentId, connections.client]);

	const refreshLinkUrl = useCallback(
		async (item: EnhancedSmartLink): Promise<EnhancedSmartLink | undefined> => {
			const productLabel = getSmartLinkProductGroupLabel(item);
			const productLinks: EnhancedSmartLink[] = [];
			for (const link of linksRef.current) {
				const isSameProduct = getSmartLinkProductGroupLabel(link) === productLabel;
				if (isSameProduct) {
					productLinks.push(link);
				}
			}

			const urlsToRefresh: string[] = [];
			const seenUrls = new Set<string>();
			for (const link of productLinks) {
				if (!seenUrls.has(link.url)) {
					seenUrls.add(link.url);
					urlsToRefresh.push(link.url);
				}
			}

			const refreshedDataByUrl = new Map<string, SmartLinkResponse>();
			await Promise.all(
				urlsToRefresh.map(async (url) => {
					try {
						const response = await connections.client.fetchData(url, true);
						refreshedDataByUrl.set(url, response);
					} catch (err: unknown) {
						reportSmartLinkError(err);
					}
				}),
			);

			if (currentContentId.current !== contentId || refreshedDataByUrl.size === 0) {
				return undefined;
			}

			let refreshedMatchingItem: EnhancedSmartLink | undefined;
			const refreshedLinks = linksRef.current.map((link) => {
				if (getSmartLinkProductGroupLabel(link) !== productLabel) {
					return link;
				}
				const response = refreshedDataByUrl.get(link.url);
				if (!response) {
					return link;
				}
				const refreshedLink = mergeRefreshedSmartLink(link, response);
				if (link.url === item.url && link.occurrenceIndex === item.occurrenceIndex) {
					refreshedMatchingItem = refreshedLink;
				}
				return refreshedLink;
			});

			linksRef.current = refreshedLinks;
			setResult((previous) => ({ ...previous, links: refreshedLinks }));
			return refreshedMatchingItem;
		},
		[connections.client, contentId],
	);

	const links = useMemo(
		() => (result.contentId === contentId ? result.links : []),
		[result.contentId, result.links, contentId],
	);
	const samePage = result.contentId === contentId;
	return {
		links,
		collectedForContentId: samePage && result.hasResolved ? contentId : undefined,
		collectedAdf: samePage ? result.adf : null,
		loading: !samePage || result.loading,
		refreshing: !samePage || result.refreshing,
		error: samePage ? result.error : null,
		refreshLinkUrl,
	};
}
